import { cn } from "@core-ui/utils/cn";
import type { DiffLineType } from "@ext/git/core/Diff/logic/model/DiffLine";
import t from "@ext/localization/locale/translate";
import { Tooltip, TooltipContent, TooltipTrigger } from "@ui-kit/Tooltip";
import type { CSSProperties, ReactNode } from "react";

export type DiffLinePlacement = { top: number; height: number; left: number | string };

const CLICK_AREA = "20px";

interface DiffLineContainerProps {
	children: ReactNode;
	type: DiffLineType;
	placement: DiffLinePlacement;
	interactive?: boolean;
	tooltipText?: string;
	tooltipOffset?: [number, number];
	onClick?: () => void;
}

export const DiffLineContainer = ({
	children,
	type,
	placement,
	interactive,
	tooltipText,
	tooltipOffset,
	onClick,
}: DiffLineContainerProps) => {
	const style: CSSProperties = { ...placement, width: CLICK_AREA };
	const label = tooltipText ?? (type === "comment" ? undefined : t(`diff.type.${type}`));
	const container = (
		<div
			aria-label={label}
			className={cn(
				"diff-line absolute opacity-50 hover:opacity-100",
				`diff-line-${type}`,
				interactive && "has-content hover:cursor-pointer",
			)}
			onClick={onClick}
			role="img"
			style={style}
		>
			{children}
		</div>
	);

	if (!tooltipText) return container;

	return (
		<Tooltip>
			<TooltipTrigger asChild>{container}</TooltipTrigger>
			{/* Centered, the tooltip drifts onto the left panel. */}
			<TooltipContent
				align="start"
				alignOffset={tooltipOffset?.[0] ?? 8}
				side="top"
				sideOffset={tooltipOffset?.[1] ?? 8}
			>
				{tooltipText}
			</TooltipContent>
		</Tooltip>
	);
};
